'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);
};

export default function HomePage() {
  useScrollReveal();

  return (
    <div className="bg-white">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/ChatGPT_Image_May_19_2026_09_43_16_PM-1779273558522.png"
            alt="Graduation ceremony"
            fill
            priority
            className="object-cover object-center transition-transform duration-700 scale-105 hover:scale-100"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24 pb-16">
          <p className="eyebrow mb-6 animate-fade-in text-[17px] text-white">
            Education &amp; Immigration Consultancy
          </p>
          <h1
            className="font-headline font-extrabold text-white mb-8 animate-fade-in"
            style={{ animationDelay: '0.1s', textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>

            Your European Journey<br />Begins Here
          </h1>
          <p
            className="font-body mb-12 max-w-3xl mx-auto animate-fade-in text-[rgba(255,255,255,0.902)] font-bold"
            style={{ fontSize: '1.25rem', lineHeight: '1.8', animationDelay: '0.2s' }}>

            Nexus Vantage is your dedicated education and immigration consultancy for studying in Spain and Portugal. We guide students from India and the GCC every step of the way — from choosing the right university to landing in Europe.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 font-body font-semibold text-white rounded-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              style={{ backgroundColor: '#2A7FA5', fontSize: '1.05rem' }}>

              Book a Free Consultation
            </Link>
            <Link
              href="/universities-spain"
              className="inline-flex items-center justify-center px-8 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1"
              style={{ border: '2px solid rgba(255,255,255,0.8)', color: '#ffffff', fontSize: '1.05rem', backgroundColor: 'rgba(255,255,255,0.1)' }}>

              Explore Universities
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.7">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </div>
      </section>
      {/* STATS BAR */}
      <section style={{ backgroundColor: '#F5EDD6' }} className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
            { number: '9', label: 'Partner Universities' },
            { number: '€700', label: 'Min. Annual Tuition' },
            { number: '2-Year', label: 'Post-Study Work Visa' },
            { number: 'EU', label: 'Residency Pathway' }]?.
            map((stat, i) =>
            <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div
                className="font-headline font-extrabold mb-2"
                style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: '#2A7FA5', lineHeight: 1 }}>

                  {stat?.number}
                </div>
                <div className="font-body font-medium text-sm uppercase tracking-wider" style={{ color: '#3d5566' }}>
                  {stat?.label}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* SERVICE CARDS */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 fade-in-up">
            <span className="eyebrow">What We Do</span>
            <h2 className="font-headline font-bold mt-3" style={{ color: '#1a2e3b' }}>
              Your Complete Support System
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
            {
              icon:
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>,

              title: 'University Guidance',
              desc: 'We help you find and apply to the right university across Madrid, Barcelona, Lisbon, and Porto'
            },
            {
              icon:
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>,

              title: 'Visa & Immigration Support',
              desc: 'Our consultants guide you through every document, form, and appointment for your Spanish or Portuguese student visa'
            },
            {
              icon:
              <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>,

              title: 'Post-Arrival Care',
              desc: 'We make sure you land, settle in, and feel at home from day one'
            }]?.
            map((card, i) =>
            <div
              key={i}
              className="fade-in-up hover-lift rounded-2xl p-8 border"
              style={{
                transitionDelay: `${i * 0.12}s`,
                borderColor: '#e0d8c8',
                backgroundColor: '#fdfaf5'
              }}>

                <div className="mb-6" style={{ color: '#2A7FA5' }}>
                  {card?.icon}
                </div>
                <h3 className="font-headline font-bold mb-4" style={{ fontSize: '1.4rem', color: '#1a2e3b' }}>
                  {card?.title}
                </h3>
                <p className="font-body text-base leading-relaxed" style={{ color: '#3d5566' }}>
                  {card?.desc}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      {/* CITY IMAGES ROW */}
      <section className="py-0">
        <div className="grid grid-cols-2 md:grid-cols-4 h-64 md:h-80">
          {[
          { src: '/assets/images/florian-wehde-WBGjg0DsO_g-unsplash-1779197309792.jpg', label: 'Madrid', href: '/universities-spain', alt: 'Madrid cityscape with iconic Gran Via boulevard at dusk' },
          { src: '/assets/images/benjamin-gremler-q_TzfAt4NQ8-unsplash-1779197319592.jpg', label: 'Barcelona', href: '/universities-spain', alt: 'Barcelona coastline with the Mediterranean sea and city skyline' },
          { src: '/assets/images/svetlana-gumerova-gOLCAOuc7iA-unsplash-1779197330178.jpg', label: 'Lisbon', href: '/universities-portugal', alt: 'Lisbon iconic bridge over the Tagus river at golden hour' },
          { src: '/assets/images/andreas-brucker-X87yB-jvYHw-unsplash-1779197352455.jpg', label: 'Porto', href: '/universities-portugal', alt: 'Porto historic Ribeira district with colourful buildings along the Douro river' }]?.
          map((city, i) =>
          <Link key={i} href={city?.href} className="relative overflow-hidden group block">
              <Image
              src={city?.src}
              alt={city?.alt}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-110" />

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="font-headline font-bold text-white text-xl">{city?.label}</span>
              </div>
            </Link>
          )}
        </div>
      </section>
      {/* CLOSING CTA STRIP */}
      <section className="py-20" style={{ backgroundColor: '#2A7FA5' }}>
        <div className="max-w-4xl mx-auto px-6 text-center fade-in-up">
          <p className="font-body text-white/90 mb-8" style={{ fontSize: '1.25rem', lineHeight: '1.8' }}>
            From your first question to your first day on campus — our consultants are with you every step of the way.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            style={{ backgroundColor: '#F5EDD6', color: '#2A7FA5', fontSize: '1.1rem' }}>

            Start Your Journey
          </Link>
        </div>
      </section>
    </div>);

}